import { getDatabase, ref, get, push, type Database } from 'firebase/database';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { CardinalDirection } from '../types/Enums';
import { ComponentLifetime, CostUnit, MaintenanceTemplate } from '../Pages/Förvaltning/TemplatesEditor/types';

/**
 * Mallkostnad change history (molnfast-react#1519).
 *
 * config/costHistory/templates/{templateId}/{pushKey}  — a template's cost + cost_unit
 * config/costHistory/lifetimes/{componentType}/{pushKey} — a type's lifetime replacement cost
 *
 * Entries are write-once (rule) and written by maintenanceTemplateApi in the same
 * root update as the cost change, only when the cost set actually changed.
 * Nothing is logged on create, copy or config import.
 */

export type CostHistoryKind = 'templates' | 'lifetimes';

/** Where a cost was changed. `draft` covers every page of a building's maintenance plan. */
export type CostChangeSource = 'draft' | 'settings' | 'templatesEditor';

export interface CostChangeContext {
    source: CostChangeSource;
    /** Set when the change came from a building's maintenance plan */
    buildingKey?: string;
}

/** A whole cost set. `directions` (lifetimes only) holds the directions that carry their own replacement cost. */
export interface CostSnapshot {
    cost: number;
    unit: CostUnit;
    directions?: Partial<Record<CardinalDirection, number>>;
}

export interface CostHistoryEntry {
    key: string;
    from: CostSnapshot;
    to: CostSnapshot;
    changedBy: string;
    changedWhen: string;
    source: CostChangeSource;
    buildingKey?: string;
}

const COST_UNITS: CostUnit[] = ['per_sqm', 'per_unit', 'per_lm'];
const SOURCES: CostChangeSource[] = ['draft', 'settings', 'templatesEditor'];
const DIRECTIONS = Object.values(CardinalDirection) as string[];

const finiteOr = (value: unknown, fallback: number): number =>
    typeof value === 'number' && Number.isFinite(value) ? value : fallback;

// Missing values read as the UI shows them: cost 0, unit kr/kvm
const unitOr = (value: unknown): CostUnit =>
    COST_UNITS.includes(value as CostUnit) ? (value as CostUnit) : 'per_sqm';

export const snapshotTemplateCost = (template: Pick<MaintenanceTemplate, 'cost' | 'cost_unit'>): CostSnapshot => ({
    cost: finiteOr(template.cost, 0),
    unit: unitOr(template.cost_unit)
});

export const snapshotLifetimeCost = (lifetime: ComponentLifetime): CostSnapshot => {
    const directions: Partial<Record<CardinalDirection, number>> = {};
    for (const dl of lifetime.directionalLifetimes ?? []) {
        if (typeof dl.replacementCost === 'number' && Number.isFinite(dl.replacementCost)) {
            directions[dl.direction] = dl.replacementCost;
        }
    }
    return {
        cost: finiteOr(lifetime.replacementCost, 0),
        unit: unitOr(lifetime.costUnit),
        ...(Object.keys(directions).length > 0 && { directions })
    };
};

export const costSnapshotsEqual = (a: CostSnapshot, b: CostSnapshot): boolean => {
    if (a.cost !== b.cost || a.unit !== b.unit) return false;
    const aDirs = a.directions ?? {};
    const bDirs = b.directions ?? {};
    const keys = new Set([...Object.keys(aDirs), ...Object.keys(bDirs)]) as Set<CardinalDirection>;
    for (const key of keys) {
        if (aDirs[key] !== bDirs[key]) return false;
    }
    return true;
};

const historyPath = (kind: CostHistoryKind, subjectKey: string): string =>
    `config/costHistory/${kind}/${subjectKey}`;

/**
 * Multi-path entries for one cost change, to merge into the same root update as
 * the change. Empty when the cost set is unchanged.
 */
export const costHistoryUpdates = (
    db: Database,
    kind: CostHistoryKind,
    subjectKey: string,
    from: CostSnapshot,
    to: CostSnapshot,
    ctx: CostChangeContext,
    uid: string
): Record<string, unknown> => {
    if (costSnapshotsEqual(from, to)) return {};
    const path = historyPath(kind, subjectKey);
    const entryKey = push(ref(db, path)).key;
    if (!entryKey) {
        throw new Error('Kunde inte skapa nyckel för kostnadshistoriken');
    }
    return {
        [`${path}/${entryKey}`]: {
            from,
            to,
            changedBy: uid,
            changedWhen: formatDateForStorage(new Date()),
            source: ctx.source,
            ...(ctx.buildingKey && { buildingKey: ctx.buildingKey })
        }
    };
};

const parseSnapshot = (data: unknown): CostSnapshot | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    if (typeof d.cost !== 'number') return null;
    const directions: Partial<Record<CardinalDirection, number>> = {};
    if (d.directions && typeof d.directions === 'object') {
        for (const [dir, value] of Object.entries(d.directions as Record<string, unknown>)) {
            if (DIRECTIONS.includes(dir) && typeof value === 'number') {
                directions[dir as CardinalDirection] = value;
            }
        }
    }
    return {
        cost: d.cost,
        unit: unitOr(d.unit),
        ...(Object.keys(directions).length > 0 && { directions })
    };
};

export const parseCostHistoryEntry = (data: unknown, key: string): CostHistoryEntry | null => {
    if (!data || typeof data !== 'object') return null;
    const d = data as Record<string, unknown>;
    const from = parseSnapshot(d.from);
    const to = parseSnapshot(d.to);
    if (!from || !to || typeof d.changedBy !== 'string' || typeof d.changedWhen !== 'string') {
        console.warn(`Ogiltig post i kostnadshistoriken: ${key}`);
        return null;
    }
    const source = SOURCES.includes(d.source as CostChangeSource) ? (d.source as CostChangeSource) : 'settings';
    return {
        key,
        from,
        to,
        changedBy: d.changedBy,
        changedWhen: d.changedWhen,
        source,
        ...(typeof d.buildingKey === 'string' && { buildingKey: d.buildingKey })
    };
};

/** A subject's history, newest first. */
export const fetchCostHistory = async (kind: CostHistoryKind, subjectKey: string): Promise<CostHistoryEntry[]> => {
    const snapshot = await get(ref(getDatabase(), historyPath(kind, subjectKey)));
    if (!snapshot.exists()) return [];
    return Object.entries(snapshot.val() as Record<string, unknown>)
        .map(([key, value]) => parseCostHistoryEntry(value, key))
        .filter((entry): entry is CostHistoryEntry => entry !== null)
        // Stockholm dates sort as strings; push keys break ties in write order
        .sort((a, b) => b.changedWhen.localeCompare(a.changedWhen) || b.key.localeCompare(a.key));
};
